import { getEnv } from '@/utils/env';
// This file has been shamelessly copied from https://github.com/brandlive1941/events-3/pull/4645/files
// accepts an input stream of characters from the incoming fetch buffer and builds them into JSON objects
// assumes the input stream is formatted as a single JSON object per line ie:
/*
{"type":"message","data":"hello"}
{"type":"message","data":"world"}
*/
// in this way we can progressively write rows in the API and have them be processed as they are received
function makeJsonDecoder() {
  let buf = '';
  let pos = 0;
  return new TransformStream({
    transform(chunk, controller) {
      buf += chunk;
      while (pos < buf.length) {
        if (buf[pos] == '\n') {
          const line = buf.substring(0, pos);
          console.log('line', line);
          controller.enqueue(JSON.parse(line));
          buf = buf.substring(pos + 1);
          pos = 0;
        } else {
          ++pos;
        }
      }
    },
  });
}

type InputMessage = {
  type?: string;
  data: never;
};

interface StateEventMap {
  [key: string]: CustomEvent;
}

interface IFetchEventTarget extends EventTarget {
  abortController: AbortController;
  addEventListener<K extends keyof StateEventMap>(
    type: K,
    listener: (ev: StateEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void;
  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean
  ): void;

  removeEventListener<K extends keyof StateEventMap>(
    type: K,
    listener: (ev: StateEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void;
  removeEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean
  ): void;
}

// accepts an input stream of JSON objects and dispatches them as events on the provided event target
function makeWriteableEventStream(eventTarget: IFetchEventTarget) {
  return new WritableStream({
    start() {
      eventTarget.dispatchEvent(new Event('start'));
    },
    write(message: InputMessage | Record<string, never> | Record<string, never>[]) {
      if (!Array.isArray(message) && message.type) {
        eventTarget.dispatchEvent(new CustomEvent(message.type, { detail: message.data }));
      } else {
        eventTarget.dispatchEvent(new CustomEvent('message', { detail: message }));
      }
    },
    close() {
      eventTarget.dispatchEvent(new CustomEvent('close'));
    },
    abort(reason) {
      eventTarget.dispatchEvent(new CustomEvent('abort', { detail: reason }));
    },
  });
}

const CleanupErr = 'cleanup';

// returns an event target where we can add event listeners for incoming event rows
// should have handlers for "start", "close", "abort", and any generic message type emitted from the API side ("message" should be the generic)
export function FetchEventTarget(input: string, init: RequestInit) {
  const eventTarget = new EventTarget() as IFetchEventTarget;

  eventTarget.abortController = new AbortController();

  init.signal = eventTarget.abortController.signal;
  try {
    fetch(input, init).then((response) => {
      console.log('FetchEventTarget: Response', response);
      const jsonDecoder = makeJsonDecoder();
      const eventStream = makeWriteableEventStream(eventTarget);

      if (!response?.body) {
        throw new Error('No response body');
      }

      response.body
        .pipeThrough(new TextDecoderStream())
        .pipeThrough(jsonDecoder)
        .pipeTo(eventStream, eventTarget.abortController)
        .catch((e) => {
          if (e !== CleanupErr) {
            console.error('FetchEventTarget: Response body error', e);
            eventTarget.dispatchEvent(new CustomEvent('Response body error', { detail: e }));
          }
        });
    });
  } catch (e) {
    console.error('TotalFetch: Error', e);
  }
  return eventTarget;
}

/**
 * FetchStreamHandler is a wrapper around FetchEventTarget that provides a fluent interface for adding event listeners
 *
 * Example usage:
 *
 * const fetchStream = FetchStream({ url: 'https://example.com', method: 'GET', token: 'my-cool-token' });
 *
 * fetchStream
 * 		.start(() => console.log('started'))
 * 		.on('some-generic-message', (data) => console.log(data))
 * 		.on('some-other-generic-message, (data) => console.log(data))
 * 		.subscribe((data) => console.log(data))
 * 		.catch((e) => console.error(e))
 * 		.ended(() => console.log('ended'));
 */
class FetchStreamHandler {
  private onDataHandler?: (e: CustomEvent) => void;
  private onErrorHandler?: (e: CustomEvent) => void;
  private onStartHandler?: (e: CustomEvent) => void;
  private onEndHandler?: () => void;
  private messageHandlers: Map<string, (e: CustomEvent) => void> = new Map();
  private target: IFetchEventTarget;

  constructor(target: IFetchEventTarget) {
    this.target = target;
    this.target.addEventListener('close', () => {
      this.cleanup();
      this.onEndHandler?.();
    });
  }

  on = (event: string, handler: (data: InputMessage) => void) => {
    const dataHandler = (e: CustomEvent) => handler(e.detail);
    this.messageHandlers.set(event, dataHandler);
    this.target.addEventListener(event, dataHandler);
    return this;
  };

  subscribe = (callback: (data: InputMessage) => void) => {
    if (this.onDataHandler) {
      throw new Error('Only one subscribe handler allowed - did you mean to use the .on handler instead?');
    }

    this.onDataHandler = (e: CustomEvent) => callback(e.detail);
    this.target.addEventListener('message', this.onDataHandler);
    return this;
  };

  catch = (callback: (e: CustomEvent) => void) => {
    if (this.onErrorHandler) {
      throw new Error('Only one error callback allowed');
    }

    this.onErrorHandler = (e: CustomEvent) => callback(e.detail);
    this.target.addEventListener('error', this.onErrorHandler);
    return this;
  };

  start = (callback: () => void) => {
    if (this.onStartHandler) {
      throw new Error('Only one start callback allowed');
    }

    const startHandler = () => {
      callback();
    };

    this.onStartHandler = startHandler;
    this.target.addEventListener('start', this.onStartHandler);
    return this;
  };

  cleanup = () => {
    if (this.onDataHandler) {
      this.target.removeEventListener('message', this.onDataHandler);
    }

    if (this.onErrorHandler) {
      this.target.removeEventListener('error', this.onErrorHandler);
    }

    if (this.onStartHandler) {
      this.target.removeEventListener('start', this.onStartHandler);
    }

    if (this.onEndHandler) {
      this.target.removeEventListener('close', this.onEndHandler);
    }

    for (const [key, handler] of this.messageHandlers) {
      this.target.removeEventListener(key, handler);
    }

    this.target.abortController.abort(CleanupErr);
  };

  ended = (callback: () => void) => {
    if (this.onEndHandler) {
      throw new Error('Only one ended callback allowed');
    }

    this.onEndHandler = callback;
    return this;
  };
}

export type FetchStreamType = InstanceType<typeof FetchStreamHandler>;

export type FetchStreamInput<T = never> = {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  token?: string;
  host?: string;
  data?: Record<string, T>;
};

// Debounce utility for async functions that blocks subsequent calls
function debounce<T extends (...args: never[]) => Promise<unknown>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => ReturnType<T> {
  let lastCallTime = 0;
  let pendingPromise: ReturnType<T> | null = null;

  return function (...args: Parameters<T>): ReturnType<T> {
    const now = Date.now();

    // If we're still within the debounce window, reject the call
    if (pendingPromise || now - lastCallTime < wait) {
      return Promise.reject(new Error('Call blocked by debounce')) as ReturnType<T>;
    }

    lastCallTime = now;
    pendingPromise = func(...args) as ReturnType<T>;

    return pendingPromise?.finally(() => {
      pendingPromise = null;
    }) as ReturnType<T>;
  };
}

async function _startDeviceAuthSession() {
  const include = getEnv().VITE_STAGE === 'local' ? undefined : 'include';
  const response = await fetch(`${getEnv().VITE_API_HOST}/v4/admin/auth/limited-input/begin`, {
    method: 'POST',
    credentials: include,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

// Debounced version with 300ms delay
export const startDeviceAuthSession = debounce(_startDeviceAuthSession, 10000);

export async function pollDeviceAuthSession(uniqueCode: string, token?: string) {
  console.log('pollDeviceAuthSession: uniqueCode', uniqueCode);
  const headers: Record<string, string> = {};
  const include = getEnv().VITE_STAGE === 'local' ? undefined : 'include';
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${getEnv().VITE_API_HOST}/v4/admin/auth/limited-input/poll?uniqueCode=${uniqueCode}`, {
    credentials: include,
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

export function FetchStream<T = never>({
  url,
  host = getEnv().VITE_API_HOST,
  token,
  data,
  method,
}: FetchStreamInput<T>): FetchStreamHandler {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  if (token) {
    headers.append('bl-token', `${token}`);
  }

  const init: RequestInit = {
    method,
    headers,
    body: JSON.stringify(data),
  };

  const target = FetchEventTarget(`${host}${url}`, init);

  return new FetchStreamHandler(target);
}
